import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FolderOpen, BookOpen, Users, CalendarDays } from 'lucide-react';
import {
  getUnits,
  getLessons,
  getClasses
} from '../firebase/lessonPlannerService';
import { getUsers } from '../firebase/userService';
import { getEvents } from '../firebase/calendarService';
import { getAssignments } from '../firebase/assignmentService';
import { isAdmin } from '../utils/permissions';
import { parseDateLocal, formatDateLocal, isSameDay } from '../utils/dateUtils';
import DashboardLayout from './DashboardLayout';
import './Dashboard.css';
import './PlannerCalendar.css';

function Dashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    classes: 0,
    units: 0,
    lessons: 0
  });
  const [classes, setClasses] = useState([]);
  const [units, setUnits] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [todayLessons, setTodayLessons] = useState([]);
  const [tomorrowLessons, setTomorrowLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  // User state
  const [currentUserData, setCurrentUserData] = useState(null);

  // Weekly curriculum state
  const [weeklyLessons, setWeeklyLessons] = useState({});

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  // Load weekly curriculum when user data is available
  useEffect(() => {
    if (currentUserData && isAdmin(currentUserData)) {
      loadWeeklyCurriculum();
    }
  }, [currentUserData]);

  const loadData = async () => {
    setLoading(true);

    // Load user data first to check permissions
    const usersResult = await getUsers();
    if (usersResult.success) {
      const userData = usersResult.users.find(u => u.email?.toLowerCase() === currentUser.email.toLowerCase());
      setCurrentUserData(userData);

      // Only load lesson data if user is admin
      if (isAdmin(userData)) {
        const [classesResult, unitsResult, lessonsResult] = await Promise.all([
          getClasses(currentUser.id),
          getUnits(currentUser.id),
          getLessons(currentUser.id)
        ]);

        const classesData = classesResult.success ? classesResult.classes : [];
        const unitsData = unitsResult.success ? unitsResult.units : [];
        const lessonsData = lessonsResult.success ? lessonsResult.lessons : [];

        setClasses(classesData);
        setUnits(unitsData);
        setLessons(lessonsData);

        setStats({
          classes: classesData.length,
          units: unitsData.length,
          lessons: lessonsData.length
        });

        // Filter lessons for today and tomorrow
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const todayLessonsFiltered = lessonsData.filter(lesson => {
          if (!lesson.scheduledDate) return false;
          const lessonDate = parseDateLocal(lesson.scheduledDate);
          return lessonDate && isSameDay(lessonDate, today);
        });

        const tomorrowLessonsFiltered = lessonsData.filter(lesson => {
          if (!lesson.scheduledDate) return false;
          const lessonDate = parseDateLocal(lesson.scheduledDate);
          return lessonDate && isSameDay(lessonDate, tomorrow);
        });

        setTodayLessons(todayLessonsFiltered);
        setTomorrowLessons(tomorrowLessonsFiltered);
      }
    }

    setLoading(false);
  };

  const loadWeeklyCurriculum = async () => {
    if (!isAdmin(currentUserData)) return;

    // Get this week's lessons (Monday - Saturday)
    const today = new Date();
    const todayDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Calculate Monday of this week
    const monday = new Date(today);
    const daysSinceMonday = todayDay === 0 ? 6 : todayDay - 1; // If Sunday, go back 6 days
    monday.setDate(today.getDate() - daysSinceMonday);
    monday.setHours(0, 0, 0, 0);

    // Get lessons, assignments, and calendar events
    const [lessonsResult, assignmentsResult, eventsResult] = await Promise.all([
      getLessons(currentUser.id),
      getAssignments(currentUser.uid),
      getEvents()
    ]);

    const lessonsData = lessonsResult.success ? lessonsResult.lessons : [];
    const assignmentsData = assignmentsResult.success ? assignmentsResult.assignments : [];
    const calendarEvents = eventsResult.success ? eventsResult.events : [];

    // Initialize week structure
    const weekLessons = {
      Monday: { date: new Date(monday), lessons: [], assignments: [] },
      Tuesday: { date: new Date(monday.getTime() + 86400000), lessons: [], assignments: [] },
      Wednesday: { date: new Date(monday.getTime() + 86400000 * 2), lessons: [], assignments: [] },
      Thursday: { date: new Date(monday.getTime() + 86400000 * 3), lessons: [], assignments: [] },
      Friday: { date: new Date(monday.getTime() + 86400000 * 4), lessons: [], assignments: [] },
      Saturday: { date: new Date(monday.getTime() + 86400000 * 5), lessons: [], assignments: [] }
    };

    // Filter lessons for this week
    lessonsData.forEach(lesson => {
      if (!lesson.scheduledDate) return;

      const lessonDate = parseDateLocal(lesson.scheduledDate);
      if (!lessonDate) return;

      // Check if lesson is this week
      Object.entries(weekLessons).forEach(([dayName, dayData]) => {
        if (isSameDay(lessonDate, dayData.date)) {
          dayData.lessons.push(lesson);
        }
      });
    });

    // Filter assignments for this week
    assignmentsData.forEach(assignment => {
      if (!assignment.assignmentDate) return;

      const assignDate = parseDateLocal(assignment.assignmentDate);
      if (!assignDate) return;

      // Check if assignment is this week
      Object.entries(weekLessons).forEach(([dayName, dayData]) => {
        if (isSameDay(assignDate, dayData.date)) {
          dayData.assignments.push(assignment);
        }
      });
    });

    // Add weekend tournaments to Saturday
    const saturday = weekLessons.Saturday.date;
    const sunday = new Date(saturday.getTime() + 86400000);
    const weekendTournaments = calendarEvents.filter(event => {
      if (event.type !== 'Tournament') return false;
      const eventDate = parseDateLocal(event.date);
      if (!eventDate) return false;
      return isSameDay(eventDate, saturday) || isSameDay(eventDate, sunday);
    });

    weekLessons.Saturday.tournaments = weekendTournaments;

    setWeeklyLessons(weekLessons);
  };

  const getClassesForLesson = (lesson) => {
    if (!lesson.unitIds || lesson.unitIds.length === 0) {
      // Handle old unitId format
      if (lesson.unitId) {
        const unit = units.find(u => u.id === lesson.unitId);
        if (unit && unit.classIds) {
          return classes.filter(c => unit.classIds.includes(c.id));
        }
      }
      return [];
    }

    // Get all units for this lesson
    const lessonUnits = units.filter(u => lesson.unitIds.includes(u.id));

    // Get all unique class IDs from those units
    const classIds = new Set();
    lessonUnits.forEach(unit => {
      if (unit.classIds) {
        unit.classIds.forEach(classId => classIds.add(classId));
      }
    });

    // Return the class objects
    return classes.filter(c => classIds.has(c.id));
  };

  const getUnitNames = (lesson) => {
    const unitIds = lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []);
    return unitIds
      .map(id => units.find(u => u.id === id)?.title)
      .filter(Boolean)
      .join(', ');
  };

  const groupLessonsByClass = (lessonsList) => {
    const grouped = {};

    lessonsList.forEach(lesson => {
      const lessonClasses = getClassesForLesson(lesson);

      if (lessonClasses.length === 0) {
        // Unassigned lessons
        if (!grouped['Unassigned']) {
          grouped['Unassigned'] = { className: 'Unassigned', lessons: [] };
        }
        grouped['Unassigned'].lessons.push(lesson);
      } else {
        lessonClasses.forEach(cls => {
          if (!grouped[cls.id]) {
            grouped[cls.id] = { className: cls.name, lessons: [] };
          }
          if (!grouped[cls.id].lessons.find(l => l.id === lesson.id)) {
            grouped[cls.id].lessons.push(lesson);
          }
        });
      }
    });

    return Object.values(grouped);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dashboard-container">
        <div className="page-header">
          <div>
            <h1>Dashboard</h1>
            <p className="subtitle">Welcome back, {currentUser?.email}</p>
          </div>
        </div>

        {/* Admin-only sections: Stats, Daily Schedule, Quick Actions */}
        {isAdmin(currentUserData) && (
          <>
            <div className="stats-grid">
              <Link to="/planner/classes" className="stat-card">
                <div className="stat-icon classes">
                  <Users size={32} />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{stats.classes}</div>
                  <div className="stat-label">Classes</div>
                </div>
              </Link>

              <Link to="/planner/units" className="stat-card">
                <div className="stat-icon units">
                  <FolderOpen size={32} />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{stats.units}</div>
                  <div className="stat-label">Units</div>
                </div>
              </Link>

              <Link to="/planner/lessons" className="stat-card">
                <div className="stat-icon lessons">
                  <BookOpen size={32} />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{stats.lessons}</div>
                  <div className="stat-label">Lessons</div>
                </div>
              </Link>
            </div>

            {/* Daily Assignment View */}
            <div className="daily-view-section">
              <h2>Daily Schedule</h2>
              <div className="daily-view-grid">
                {/* Today's Lessons */}
                <div className="day-column">
                  <div className="day-header today">
                    <h3>Today</h3>
                    <span className="date-label">{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="day-content">
                    {todayLessons.length === 0 ? (
                      <div className="no-lessons">
                        <p>No lessons scheduled for today</p>
                      </div>
                    ) : (
                      groupLessonsByClass(todayLessons).map((group, idx) => (
                        <div key={idx} className="class-group">
                          <div className="class-group-header">
                            <Users size={16} />
                            <span className="class-name">{group.className}</span>
                          </div>
                          {group.lessons.map(lesson => (
                            <div key={lesson.id} className="lesson-item">
                              <div className="lesson-title">{lesson.title}</div>
                              {getUnitNames(lesson) && (
                                <div className="lesson-unit">{getUnitNames(lesson)}</div>
                              )}
                              {lesson.duration && (
                                <div className="lesson-duration">{lesson.duration} min</div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Tomorrow's Lessons */}
                <div className="day-column">
                  <div className="day-header tomorrow">
                    <h3>Tomorrow</h3>
                    <span className="date-label">{new Date(Date.now() + 86400000).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="day-content">
                    {tomorrowLessons.length === 0 ? (
                      <div className="no-lessons">
                        <p>No lessons scheduled for tomorrow</p>
                      </div>
                    ) : (
                      groupLessonsByClass(tomorrowLessons).map((group, idx) => (
                        <div key={idx} className="class-group">
                          <div className="class-group-header">
                            <Users size={16} />
                            <span className="class-name">{group.className}</span>
                          </div>
                          {group.lessons.map(lesson => (
                            <div key={lesson.id} className="lesson-item">
                              <div className="lesson-title">{lesson.title}</div>
                              {getUnitNames(lesson) && (
                                <div className="lesson-unit">{getUnitNames(lesson)}</div>
                              )}
                              {lesson.duration && (
                                <div className="lesson-duration">{lesson.duration} min</div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="quick-actions">
              <h2>Quick Actions</h2>
              <div className="actions-grid">
                <Link to="/planner/units" className="action-card">
                  <FolderOpen size={24} />
                  <span>Manage Units</span>
                </Link>
                <Link to="/planner/lessons" className="action-card">
                  <BookOpen size={24} />
                  <span>Create Lesson</span>
                </Link>
                <Link to="/planner/schedule" className="action-card">
                  <CalendarDays size={24} />
                  <span>View Schedule</span>
                </Link>
                <Link to="/calendar" className="action-card">
                  <CalendarDays size={24} />
                  <span>Calendar</span>
                </Link>
              </div>
            </div>
          </>
        )}


        {/* Weekly Curriculum Preview - Admin only */}
        {isAdmin(currentUserData) && Object.keys(weeklyLessons).length > 0 && (
          <div className="weekly-curriculum-section">
            <h2>This Week's Debate 1 Curriculum</h2>
            <div className="week-grid">
              {Object.entries(weeklyLessons).map(([dayName, dayData]) => {
                const isToday = dayData.date && dayData.date.toDateString() === new Date().toDateString();
                const dayNumber = dayData.date?.getDate();
                const monthName = dayData.date?.toLocaleDateString('en-US', { month: 'short' });
                const isSaturday = dayName === 'Saturday';

                return (
                  <div key={dayName} className={`week-day-card ${isToday ? 'today' : ''} ${isSaturday ? 'saturday-card' : ''}`}>
                    <div className="week-day-header">
                      <div className="week-day-name">{dayName.substring(0, 3).toUpperCase()}</div>
                      <div className="week-day-number">
                        {monthName} {dayNumber}
                      </div>
                    </div>
                    <div className="week-day-lessons">
                      {isSaturday ? (
                        // Saturday special content - show tournaments
                        dayData.tournaments && dayData.tournaments.length > 0 ? (
                          <div className="tournament-info">
                            {dayData.tournaments.map(tournament => (
                              <div key={tournament.id} className="tournament-item">
                                <div className="tournament-icon">🏆</div>
                                <div className="tournament-details">
                                  <div className="tournament-name">{tournament.title}</div>
                                  <div className="tournament-date">
                                    {new Date(tournament.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-weekend-events">
                            <div className="no-events-icon">🎉</div>
                            <div className="no-events-text">No Events This Weekend!</div>
                          </div>
                        )
                      ) : (
                        // Weekday content - show lessons and assignments
                        (!dayData.lessons || dayData.lessons.length === 0) && (!dayData.assignments || dayData.assignments.length === 0) ? (
                          <div className="week-no-lessons">No lessons or assignments</div>
                        ) : (
                          <>
                            {dayData.lessons && dayData.lessons.map(lesson => (
                              <div key={lesson.id} className="week-lesson-item">
                                <div className="week-lesson-content">
                                  <div className="week-lesson-title">📚 {lesson.title}</div>
                                </div>
                                {lesson.resources && lesson.resources.length > 0 && (
                                  <button
                                    className="week-lesson-resource-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (lesson.resources.length === 1) {
                                        window.open(lesson.resources[0].fileUrl || lesson.resources[0].url, '_blank');
                                      } else {
                                        // Show first resource
                                        window.open(lesson.resources[0].fileUrl || lesson.resources[0].url, '_blank');
                                      }
                                    }}
                                    title={lesson.resources.length === 1 ? 'Open resource' : `${lesson.resources.length} resources`}
                                  >
                                    🔗
                                    {lesson.resources.length > 1 && <span className="resource-badge">{lesson.resources.length}</span>}
                                  </button>
                                )}
                              </div>
                            ))}
                            {dayData.assignments && dayData.assignments.map(assignment => (
                              <div key={assignment.id} className="week-lesson-item week-assignment-item">
                                <div className="week-lesson-content">
                                  <div className="week-lesson-title" style={{ color: '#d97706' }}>
                                    📝 {assignment.title}
                                  </div>
                                  {assignment.classNames && (
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>
                                      {assignment.classNames.join(', ')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Dashboard;
